import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { organizationSchema } from '../lib/schema';
import { sendToWhatsApp, formatMessage, whatsappLink, track } from '../lib/contact';
import { PageMeta, PageHero, Icon, WhatsAppIcon, Field, describedBy } from '../components/ui';

const TOPICS = ['Booking a wash', 'Existing booking', 'Monthly plans', 'Fleet / corporate', 'Partnering with Quick Way', 'Something else'];

const Contact = () => {
  const { BUSINESS } = useSite();
  const [form, setForm] = useState({ name: '', topic: TOPICS[0], message: '' });
  const [errors, setErrors] = useState({});
  const [sentUrl, setSentUrl] = useState(null);
  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (ev) => {
    ev.preventDefault();
    const e = {};
    if (!form.name.trim()) e.name = 'Enter your name.';
    if (form.message.trim().length < 5) e.message = 'Write a short message.';
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(Object.keys(e)[0])?.focus();
      return;
    }
    const text = formatMessage(`Hi Quick Way, I have a question about: ${form.topic}`, [['Name', form.name], ['Message', form.message]]);
    setSentUrl(sendToWhatsApp(text, 'contact_whatsapp', { topic: form.topic }));
  };

  return (
    <>
      <PageMeta
        title="Contact Quick Way | Mobile Car Wash Abu Dhabi & Dubai"
        description={`Contact Quick Way on WhatsApp or call ${BUSINESS.phoneDisplay} to book a mobile car wash in Abu Dhabi or Dubai. Email ${BUSINESS.email}.`}
        path="/contact"
        crumbs={[{ name: 'Contact', path: '/contact' }]}
        jsonLd={[organizationSchema()]}
      />
      <PageHero eyebrow="Contact" title="Contact Quick Way">
        <p>The fastest way to reach us is WhatsApp. You can also call or email.</p>
      </PageHero>

      <section className="section">
        <div className="container split split--top">
          <div className="stack">
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="contact-card" onClick={() => track('whatsapp_click', { source: 'contact' })}>
              <span className="icon-tile icon-tile--wa"><WhatsAppIcon size={24} /></span>
              <span><strong>WhatsApp</strong><span>{BUSINESS.phoneDisplay}</span></span>
            </a>
            <a href={`tel:${BUSINESS.phone}`} className="contact-card" onClick={() => track('call_click', { source: 'contact' })}>
              <span className="icon-tile"><Icon name="phone" /></span>
              <span><strong>Phone</strong><span>{BUSINESS.phoneDisplay}</span></span>
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="contact-card">
              <span className="icon-tile"><Icon name="chat" /></span>
              <span><strong>Email</strong><span>{BUSINESS.email}</span></span>
            </a>
            <div className="contact-card contact-card--static">
              <span className="icon-tile"><Icon name="pin" /></span>
              <span><strong>Service areas</strong><span>Abu Dhabi and Dubai. We come to your location. <Link to="/locations">See areas</Link></span></span>
            </div>
          </div>

          <div className="form-card">
            <h2 className="form-card__title">Send us a message</h2>
            {sentUrl ? (
              <div className="form-success" role="status">
                <Icon name="chat" size={28} />
                <h3>Press send in WhatsApp</h3>
                <p>Your message is ready in WhatsApp. It reaches us once you press send.</p>
                <a href={sentUrl} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">Open WhatsApp</a>
                <button type="button" className="btn-link" onClick={() => setSentUrl(null)}>Write another message</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <Field label="Name" id="name" error={errors.name} required>
                  <input id="name" name="name" value={form.name} onChange={set} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={describedBy('name', errors.name)} />
                </Field>
                <Field label="Topic" id="topic">
                  <select id="topic" name="topic" value={form.topic} onChange={set}>
                    {TOPICS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Message" id="message" error={errors.message} required>
                  <textarea id="message" name="message" rows="5" value={form.message} onChange={set} aria-invalid={!!errors.message} aria-describedby={describedBy('message', errors.message)} />
                </Field>
                <button type="submit" className="btn btn-primary btn-block btn-lg">Send via WhatsApp</button>
                <p className="field__hint">Want to book? The <Link to="/book">booking form</Link> collects everything we need in one go.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
